import React, { forwardRef, useRef, useImperativeHandle } from 'react';

import { ariaUtils, keyboardNavigation } from '../../utils/accessibility';
import { cn } from '../../utils/cn';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'outlined' | 'filled';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  rounded?: boolean;
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
  disabled?: boolean;
  // Accessibility props
  as?:
    | 'div'
    | 'article'
    | 'section'
    | 'aside'
    | 'main'
    | 'header'
    | 'footer'
    | 'nav';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-selected'?: boolean;
  focusable?: boolean;
  landmark?: boolean;
}

const variants = {
  default:
    'bg-[var(--se-porcelain-panel)] border border-[var(--se-rule)]',
  elevated:
    'bg-[var(--se-porcelain-panel)] border border-[var(--se-rule)] shadow-sm',
  outlined:
    'bg-[var(--se-porcelain-panel)] border-2 border-[var(--se-rule)]',
  filled: 'bg-[var(--se-porcelain)] border border-[var(--se-rule)]',
};

const paddings = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

const shadows = {
  none: '',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
};

export interface CardRef {
  focus: () => void;
  blur: () => void;
  scrollIntoView: (options?: ScrollIntoViewOptions) => void;
}

export const Card = forwardRef<CardRef, CardProps>(
  (
    {
      children,
      variant = 'default',
      padding = 'md',
      rounded = true,
      shadow = 'none',
      interactive = false,
      disabled = false,
      as: Component = 'div',
      className,
      onClick,
      onKeyDown,
      focusable,
      landmark = false,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': ariaExpanded,
      'aria-selected': ariaSelected,
      ...props
    },
    ref
  ) => {
    const cardRef = useRef<HTMLDivElement>(null);

    // Expose card methods through ref
    useImperativeHandle(ref, () => ({
      focus: () => cardRef.current?.focus(),
      blur: () => cardRef.current?.blur(),
      scrollIntoView: (options?: ScrollIntoViewOptions) => {
        cardRef.current?.scrollIntoView(options);
      },
    }));

    // Determine if card should be focusable
    const isFocusable = focusable || interactive || onClick;
    const isDisabled = disabled;

    // Enhanced click handler
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    // Enhanced keyboard handler
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }

      // Handle activation if interactive
      if (interactive || onClick) {
        keyboardNavigation.handleActivation(event.nativeEvent, () => {
          cardRef.current?.click();
        });
      }

      onKeyDown?.(event);
    };

    // Generate unique ID for accessibility
    const cardId = ariaUtils.generateId('card');

    const baseClasses = cn(
      // Base styles
      'relative transition-colors duration-200',

      // Variant
      variants[variant],

      // Padding
      paddings[padding],

      // Rounded corners
      rounded && 'rounded-lg',

      // Shadow
      shadows[shadow],

      // Interactive states
      interactive &&
        !isDisabled && [
          'cursor-pointer',
          'hover:shadow-md hover:border-gray-300',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          'active:transform active:scale-[0.98]',
        ],

      // Disabled state
      isDisabled && ['opacity-60 cursor-not-allowed', 'pointer-events-none'],

      // Focus styles for non-interactive but focusable cards
      isFocusable &&
        !interactive &&
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',

      className
    );

    // Determine semantic role
    const getRole = () => {
      if (interactive || onClick) {
        return 'button';
      }
      if (landmark) {
        return 'region';
      }
      if (Component === 'article' || Component === 'section') {
        return undefined; // Let semantic HTML handle it
      }
      return 'group';
    };

    // Determine tabIndex
    const getTabIndex = () => {
      if (isDisabled) return -1;
      if (isFocusable) return 0;
      return undefined;
    };

    return (
      <Component
        ref={cardRef}
        id={cardId}
        className={baseClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role={getRole()}
        tabIndex={getTabIndex()}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-selected={ariaSelected}
        aria-disabled={isDisabled}
        {...props}
      >
        {children}

        {/* Screen reader helper for interactive cards */}
        {interactive && !ariaLabel && !ariaLabelledBy && (
          <span className="sr-only">
            Interactive card - press Enter or Space to activate
          </span>
        )}

        {/* Disabled state indicator for screen readers */}
        {isDisabled && <span className="sr-only">(disabled)</span>}
      </Component>
    );
  }
);

Card.displayName = 'Card';

// Card subcomponents for better semantic structure
export const CardHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <header
    ref={ref}
    className={cn('border-b border-gray-200 pb-3 mb-4', className)}
    {...props}
  >
    {children}
  </header>
));
CardHeader.displayName = 'CardHeader';

export const CardTitle = forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    level?: 1 | 2 | 3 | 4 | 5 | 6;
  }
>(({ children, className, level = 2, ...props }, ref) => {
  const Heading = `h${level}` as const;

  return (
    <Heading
      ref={ref}
      className={cn('text-lg font-semibold text-gray-900', className)}
      {...props}
    >
      {children}
    </Heading>
  );
});
CardTitle.displayName = 'CardTitle';

export const CardContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <div ref={ref} className={cn('text-gray-600', className)} {...props}>
    {children}
  </div>
));
CardContent.displayName = 'CardContent';

export const CardFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => (
  <footer
    ref={ref}
    className={cn('border-t border-gray-200 pt-3 mt-4', className)}
    {...props}
  >
    {children}
  </footer>
));
CardFooter.displayName = 'CardFooter';
